import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useConfig } from '../context/ConfigContext';
import { formatOrdinalDay } from '../utils/formatters';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Default form settings
const DEFAULT_SETTINGS = {
  pageTitle: 'Congratulations on Your Winning Bid!',
  pageDescription: 'Please complete this form so we can finalize your land purchase',
  successTitle: 'Thank You for Your Submission!',
  successMessage: "We've received your information and will contact you within 24 hours to complete your registration and finalize your financing terms.",
  requiredFields: {
    firstName: true,
    lastName: true,
    email: true,
    phone: true,
    propertyTitle: true,
    auctionUrl: false,
    ebayUsername: true,
    winningBid: true,
    preferredDueDay: true,
    mailingAddress: false,
    mailingCity: false,
    mailingState: false,
    mailingZip: false,
    notes: false
  }
};

function EbayWinnerForm() {
  const { getTenantId, getColor, getPaymentDueDayMode, getPaymentDueDayOptions } = useConfig();
  // Two hardcoded 1/15 options used to live in the select below. A tenant selling
  // the 10th and the 20th collected a "preferred" day it does not offer, and an
  // anniversary-mode tenant collected a preference it never honours at all.
  const dueDayMode = getPaymentDueDayMode();
  const isAnniversaryDueDay = dueDayMode === 'anniversary';
  const offeredDueDays = getPaymentDueDayOptions();
  const tenantId = getTenantId();
  const primaryColor = getColor('primaryColor') || '#2c5f2d';
  const primaryDark = getColor('primaryDark') || '#1e4620';
  // Brand colour as TEXT, which is a different job from brand colour as a FILL.
  // primaryColor stays raw below for the page gradient, the buttons and the
  // borders, because a brand fill is preserved in both themes and white text on
  // it stays correct. Headings are the opposite case: raw brand navy measures
  // 1.31:1 against the dark surface, and these headings were the last thing in
  // the app still failing in dark mode. No literal sweep could have caught them,
  // because the colour arrives as a JS value from tenant config rather than as a
  // hex in the source. --color-brand-text resolves to the brand colour itself in
  // light mode, so light is unchanged.
  const brandTextColor = 'var(--color-brand-text)';

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Fetch form settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      if (!tenantId) {
        setSettingsLoaded(true);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/ebay/form-settings/public/${tenantId}`);
        if (response.data.settings) {
          setSettings({
            ...DEFAULT_SETTINGS,
            ...response.data.settings,
            requiredFields: {
              ...DEFAULT_SETTINGS.requiredFields,
              ...(response.data.settings.requiredFields || {})
            }
          });
        }
      } catch (err) {
        console.error('Error fetching form settings:', err);
        // Use defaults on error
      } finally {
        setSettingsLoaded(true);
      }
    };

    fetchSettings();
  }, [tenantId]);

  // Helper to check if a field is required
  const isRequired = (fieldName) => settings.requiredFields[fieldName] ?? false;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    propertyTitle: '',
    auctionUrl: '',
    ebayUsername: '',
    winningBid: '',
    preferredDueDay: '1',
    mailingAddress: '',
    mailingCity: '',
    mailingState: '',
    mailingZip: '',
    notes: ''
  });

  // Config arrives asynchronously, so the initial '1' can be a day this tenant
  // does not offer. Snap to the first offered day, exactly as the purchase page
  // does, so the submission records a preference the tenant can actually honour.
  useEffect(() => {
    if (isAnniversaryDueDay) return;
    if (!Array.isArray(offeredDueDays) || offeredDueDays.length === 0) return;
    setFormData(prev => (offeredDueDays.map(String).includes(prev.preferredDueDay)
      ? prev
      : { ...prev, preferredDueDay: String(offeredDueDays[0]) }));
  }, [isAnniversaryDueDay, offeredDueDays]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    if (!tenantId) {
      setError('This form is not linked to a seller yet. Please use the link provided in your auction.');
      setSubmitting(false);
      return;
    }

    try {
      // tenantId is required: the server scopes every read of this table by it,
      // so a submission without one would never reach the seller's admin.
      const response = await axios.post(`${API_URL}/ebay/winner-submission`, { ...formData, tenantId });
      
      if (response.data.success) {
        setSubmitted(true);
        window.scrollTo(0, 0);
      }
    } catch (err) {
      console.error('Submission error:', err);
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${primaryColor}10 0%, ${primaryColor}20 100%)`, padding: '40px 20px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{
            background: 'var(--color-surface)',
            borderRadius: '15px',
            padding: '50px 30px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '80px', marginBottom: '20px' }}>✅</div>
            <h1 style={{ color: brandTextColor, fontSize: '32px', marginBottom: '20px' }}>
              {settings.successTitle}
            </h1>
            <p style={{ fontSize: '18px', lineHeight: '1.8', color: 'var(--color-text-light)', marginBottom: '30px' }}>
              {settings.successMessage}
            </p>
            <div style={{
              background: `${primaryColor}10`,
              border: `2px solid ${primaryColor}`,
              borderRadius: '10px',
              padding: '25px',
              marginBottom: '30px',
              textAlign: 'left'
            }}>
              <h3 style={{ color: brandTextColor, marginTop: 0 }}>📋 Next Steps:</h3>
              <ol style={{ fontSize: '16px', lineHeight: '1.8', paddingLeft: '25px' }}>
                <li>Check your email for a confirmation (check spam if needed)</li>
                <li>We'll call you within 24 hours to verify your information</li>
                <li>Complete your account registration on our website</li>
                <li>Choose your final financing terms</li>
                <li>Make your down payment (your winning bid amount)</li>
                <li>Review and sign your Contract for Deed</li>
                <li>Start enjoying your land!</li>
              </ol>
            </div>
            <a
              href="/"
              style={{
                display: 'inline-block',
                padding: '15px 40px',
                background: primaryColor,
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontSize: '18px',
                fontWeight: 'bold',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.background = primaryDark}
              onMouseOut={(e) => e.target.style.background = primaryColor}
            >
              Return to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while settings are being fetched
  if (!settingsLoaded) {
    return (
      <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${primaryColor}10 0%, ${primaryColor}20 100%)`, padding: '40px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'var(--color-text-light)' }}>
          <div style={{ fontSize: '24px', marginBottom: '10px' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${primaryColor}10 0%, ${primaryColor}20 100%)`, padding: '40px 20px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          background: 'var(--color-surface)',
          borderRadius: '15px 15px 0 0',
          padding: '40px 30px',
          textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
          <h1 style={{ color: brandTextColor, fontSize: '36px', marginBottom: '10px' }}>
            🎉 {settings.pageTitle}
          </h1>
          <p style={{ fontSize: '18px', color: 'var(--color-text-light)', lineHeight: '1.6', marginBottom: 0 }}>
            {settings.pageDescription}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ 
          background: 'var(--color-surface)', 
          borderRadius: '0 0 15px 15px', 
          padding: '40px 30px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
          {error && (
            <div style={{ 
              background: 'var(--color-error-bg)', 
              border: '2px solid var(--color-error-border)', 
              borderRadius: '8px', 
              padding: '15px', 
              marginBottom: '25px',
              color: 'var(--color-error-text)'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Contact Information Section */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{
              color: brandTextColor,
              fontSize: '24px',
              marginBottom: '20px',
              paddingBottom: '10px',
              borderBottom: '3px solid #f4a460'
            }}>
              📞 Contact Information
            </h2>

            <div className="form-row-2">
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--color-text)' }}>
                  First Name {isRequired('firstName') && '*'}
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required={isRequired('firstName')}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = primaryColor}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--color-text)' }}>
                  Last Name {isRequired('lastName') && '*'}
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required={isRequired('lastName')}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = primaryColor}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                />
              </div>
            </div>

            <div className="form-row-2">
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--color-text)' }}>
                  Email {isRequired('email') && '*'}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required={isRequired('email')}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = primaryColor}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--color-text)' }}>
                  Phone {isRequired('phone') && '*'}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required={isRequired('phone')}
                  placeholder="(920) 555-1234"
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = primaryColor}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                />
              </div>
            </div>
          </div>

          {/* Auction Details Section */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{
              color: brandTextColor,
              fontSize: '24px',
              marginBottom: '20px',
              paddingBottom: '10px',
              borderBottom: '3px solid #f4a460'
            }}>
              🏆 Auction Details
            </h2>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--color-text)' }}>
                Property Title {isRequired('propertyTitle') && '*'}
              </label>
              <input
                type="text"
                name="propertyTitle"
                value={formData.propertyTitle}
                onChange={handleChange}
                required={isRequired('propertyTitle')}
                placeholder="e.g., 5 Acres Arizona Land Near Phoenix"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '2px solid var(--color-border)',
                  borderRadius: '8px',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = primaryColor}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              />
              <small style={{ color: 'var(--color-text-light)', fontSize: '14px' }}>
                Copy the title from your eBay auction
              </small>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--color-text)' }}>
                eBay Auction URL {isRequired('auctionUrl') ? '*' : '(optional)'}
              </label>
              <input
                type="url"
                name="auctionUrl"
                value={formData.auctionUrl}
                onChange={handleChange}
                required={isRequired('auctionUrl')}
                placeholder="https://www.ebay.com/itm/..."
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '2px solid var(--color-border)',
                  borderRadius: '8px',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = primaryColor}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              />
              <small style={{ color: 'var(--color-text-light)', fontSize: '14px' }}>
                Must include https:// (example: https://www.ebay.com/itm/...)
              </small>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--color-text)' }}>
                eBay Username {isRequired('ebayUsername') && '*'}
              </label>
              <input
                type="text"
                name="ebayUsername"
                value={formData.ebayUsername}
                onChange={handleChange}
                required={isRequired('ebayUsername')}
                placeholder="your_ebay_username"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '2px solid var(--color-border)',
                  borderRadius: '8px',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = primaryColor}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              />
              <small style={{ color: 'var(--color-text-light)', fontSize: '14px' }}>
                Your eBay username so we can verify the winning bid
              </small>
            </div>

            <div className="form-row-2">
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--color-text)' }}>
                  Your Winning Bid Amount {isRequired('winningBid') && '*'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="winningBid"
                  value={formData.winningBid}
                  onChange={handleChange}
                  required={isRequired('winningBid')}
                  placeholder="99.00"
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = primaryColor}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                />
                <small style={{ color: 'var(--color-text-light)', fontSize: '14px' }}>
                  This becomes your down payment
                </small>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--color-text)' }}>
                  {isAnniversaryDueDay ? 'Payment Due Day' : 'Preferred Payment Due Day'}
                </label>
                {isAnniversaryDueDay ? (
                  /* NOT A CHOICE IN ANNIVERSARY MODE. Asking for a preference
                     the loan routes will discard is the same defect the purchase
                     page's picker had before Stage 2a. */
                  <p style={{ margin: 0, fontSize: '16px', lineHeight: 1.5, color: 'var(--color-text)' }}>
                    Your payments will fall on the anniversary of your purchase date,
                    the same day of each month that your purchase completes.
                  </p>
                ) : (
                  <select
                    name="preferredDueDay"
                    value={formData.preferredDueDay}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      border: '2px solid var(--color-border)',
                      borderRadius: '8px',
                      transition: 'border-color 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = primaryColor}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  >
                    {offeredDueDays.map(day => (
                      <option key={day} value={String(day)}>{formatOrdinalDay(day)} of month</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>

          {/* Mailing Address Section */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{
              color: brandTextColor,
              fontSize: '24px',
              marginBottom: '20px',
              paddingBottom: '10px',
              borderBottom: '3px solid #f4a460'
            }}>
              📬 Mailing Address {!(isRequired('mailingAddress') || isRequired('mailingCity') || isRequired('mailingState') || isRequired('mailingZip')) && '(optional - can be added later)'}
            </h2>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--color-text)' }}>
                Street Address {isRequired('mailingAddress') ? '*' : ''}
              </label>
              <input
                type="text"
                name="mailingAddress"
                value={formData.mailingAddress}
                onChange={handleChange}
                required={isRequired('mailingAddress')}
                placeholder="123 Main Street"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '2px solid var(--color-border)',
                  borderRadius: '8px',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = primaryColor}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              />
            </div>

            <div className="form-row-wide-2">
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--color-text)' }}>
                  City {isRequired('mailingCity') ? '*' : ''}
                </label>
                <input
                  type="text"
                  name="mailingCity"
                  value={formData.mailingCity}
                  onChange={handleChange}
                  required={isRequired('mailingCity')}
                  placeholder="Appleton"
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = primaryColor}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--color-text)' }}>
                  State {isRequired('mailingState') ? '*' : ''}
                </label>
                <input
                  type="text"
                  name="mailingState"
                  value={formData.mailingState}
                  onChange={handleChange}
                  required={isRequired('mailingState')}
                  placeholder="WI"
                  maxLength="2"
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = primaryColor}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--color-text)' }}>
                  ZIP {isRequired('mailingZip') ? '*' : ''}
                </label>
                <input
                  type="text"
                  name="mailingZip"
                  value={formData.mailingZip}
                  onChange={handleChange}
                  required={isRequired('mailingZip')}
                  placeholder="54911"
                  maxLength="10"
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = primaryColor}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                />
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div style={{ marginBottom: '40px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--color-text)' }}>
              Additional Notes or Questions {isRequired('notes') ? '*' : '(optional)'}
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              required={isRequired('notes')}
              rows="4"
              placeholder="Any questions or special requests?"
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                border: '2px solid var(--color-border)',
                borderRadius: '8px',
                resize: 'vertical',
                fontFamily: 'inherit',
                transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = primaryColor}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '18px',
              fontSize: '20px',
              fontWeight: 'bold',
              background: submitting ? '#ccc' : primaryColor,
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: `0 4px 12px ${primaryColor}50`
            }}
            onMouseOver={(e) => {
              if (!submitting) e.target.style.background = primaryDark;
            }}
            onMouseOut={(e) => {
              if (!submitting) e.target.style.background = primaryColor;
            }}
          >
            {submitting ? 'Submitting...' : '✅ Submit Information'}
          </button>

          <p style={{ 
            textAlign: 'center', 
            marginTop: '20px', 
            color: 'var(--color-text-light)', 
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            By submitting this form, you confirm that you are the winning bidder of the eBay auction<br/>
            and agree to complete the purchase per the terms listed in the auction.
          </p>
        </form>
      </div>
    </div>
  );
}

export default EbayWinnerForm;